import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaHistory, FaSyncAlt, FaArrowUp, FaArrowDown, FaFlask } from 'react-icons/fa';
import EvaluationPanel from './EvaluationPanel';
import './AssistantVersions.css';

// Instruction versions of an assistant and what each one did (§14 B):
// runs, approval rate of the drafts, mean size of the human corrections,
// status overrides, errors, latency, cost — with the change against the
// previous version. Data: GET /api/ai-runs/assistants/{id}/versions.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const pct = (value) => (typeof value === 'number' ? `${Math.round(value * 100)} %` : '—');
const num = (value, lang) => (typeof value === 'number' ? value.toLocaleString(lang) : '—');
const ms = (value) => (typeof value === 'number' ? (value < 1000 ? `${Math.round(value)} ms` : `${(value / 1000).toFixed(1)} s`) : '—');
const cost = (value) => (typeof value === 'number' ? `$${value.toFixed(4)}` : null);

// A delta chip: `good` says whether an increase is desirable.
const Delta = ({ value, good, format }) => {
    if (typeof value !== 'number' || value === 0) return null;
    const positive = value > 0;
    const desirable = good ? positive : !positive;
    return (
        <span className={`asv-delta ${desirable ? 'better' : 'worse'}`} title={format(value)}>
            {positive ? <FaArrowUp /> : <FaArrowDown />} {format(Math.abs(value))}
        </span>
    );
};

const AssistantVersions = ({ assistantId, currentInstructions, onApplied }) => {
    const [evaluating, setEvaluating] = useState(false);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (!assistantId) return;
        setError(null);
        try {
            const response = await axios.get(`/api/ai-runs/assistants/${assistantId}/versions?days=${days}`, authHeaders());
            setData(response.data);
        } catch (e) {
            setData({ versions: [] });
            setError(t('assistantVersions.loadError', 'Impossible de charger les versions.'));
        }
    }, [assistantId, days, t]);

    useEffect(() => { load(); }, [load]);

    const versions = data?.versions || [];
    const withRuns = versions.filter((v) => v.metrics?.runs > 0);

    return (
        <section className="assistant-versions">
            <div className="asv-header">
                <h3><FaHistory /> {t('assistantVersions.title', 'Versions des instructions')}</h3>
                <div className="asv-actions">
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="asv-select">
                        {[30, 90, 365].map((d) => (
                            <option key={d} value={d}>{t('assistantVersions.lastDays', '{{days}} derniers jours', { days: d })}</option>
                        ))}
                    </select>
                    <button type="button" className="u-btn u-btn--secondary" onClick={() => setEvaluating(true)}>
                        <FaFlask /> {t('assistantVersions.evaluate', 'Évaluer un changement')}
                    </button>
                    <button type="button" className="asv-icon" onClick={load} title={t('assistantVersions.refresh', 'Actualiser')}><FaSyncAlt /></button>
                </div>
            </div>
            <p className="asv-intro">
                {t('assistantVersions.intro', 'Chaque modification des instructions est une version. Pour chacune : ce que l’IA a fait avec, et ce que les opérateurs en ont corrigé. Les flèches comparent à la version précédente.')}
            </p>
            {error && <div className="asv-error">{error}</div>}
            {data && versions.length === 0 && !error && (
                <div className="asv-empty">{t('assistantVersions.empty', 'Aucune version enregistrée pour cet assistant.')}</div>
            )}
            {versions.length > 0 && (
                <div className="asv-table-wrap">
                    <table className="asv-table">
                        <thead>
                            <tr>
                                <th>{t('assistantVersions.colVersion', 'Version')}</th>
                                <th>{t('assistantVersions.colOrigin', 'Origine')}</th>
                                <th className="num">{t('assistantVersions.colRuns', 'Runs')}</th>
                                <th className="num">{t('assistantVersions.colTickets', 'Tickets')}</th>
                                <th className="num">{t('assistantVersions.colApproval', 'Brouillons approuvés')}</th>
                                <th className="num">{t('assistantVersions.colEdit', 'Correction moyenne')}</th>
                                <th className="num">{t('assistantVersions.colCarrier', 'Messages transporteur validés')}</th>
                                <th className="num">{t('assistantVersions.colOverrides', 'Statuts repris')}</th>
                                <th className="num">{t('assistantVersions.colErrors', 'Erreurs / run')}</th>
                                <th className="num">{t('assistantVersions.colLatency', 'Durée moy.')}</th>
                                {withRuns.some((v) => typeof v.metrics?.cost_usd_per_ticket === 'number') && (
                                    <th className="num">{t('assistantVersions.colCost', 'Coût / ticket')}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {versions.map((v) => {
                                const m = v.metrics || {};
                                const current = data?.assistant?.current_hash && v.prompt_hash === data.assistant.current_hash;
                                const changedAt = v.changed_at ? new Date(v.changed_at).toLocaleString(lang) : '—';
                                return (
                                    <tr key={`${v.version}-${v.prompt_hash}`} className={`${current ? 'current' : ''} ${m.runs ? '' : 'idle'}`}>
                                        <td>
                                            <strong>{v.version ? `v${v.version}` : t('assistantVersions.unknown', 'inconnue')}</strong>
                                            {current && <span className="asv-pill">{t('assistantVersions.current', 'actuelle')}</span>}
                                            <div className="asv-muted">{changedAt}</div>
                                        </td>
                                        <td>
                                            {t(`assistantVersions.origin.${v.origin}`, v.origin || '—')}
                                            {v.actor?.type && v.actor.type !== 'system' && (
                                                <div className="asv-muted" title={v.actor.email || ''}>{v.actor.name || v.actor.email || v.actor.type}</div>
                                            )}
                                        </td>
                                        <td className="num">{num(m.runs, lang)}</td>
                                        <td className="num">{num(m.tickets, lang)}</td>
                                        <td className="num">
                                            {pct(m.approval?.drafts?.approval_rate)}
                                            <span className="asv-muted"> / {num(m.approval?.drafts?.reviewed, lang)}</span>
                                            <Delta value={v.deltas?.draft_approval_rate} good format={(x) => `${Math.round(x * 100)} pt`} />
                                        </td>
                                        <td className="num">{pct(m.edit_ratio_mean)}<Delta value={v.deltas?.edit_ratio_mean} good={false} format={(x) => `${Math.round(x * 100)} pt`} /></td>
                                        <td className="num">
                                            {pct(m.approval?.carrier_messages?.approval_rate)}
                                            <span className="asv-muted"> / {num(m.approval?.carrier_messages?.reviewed, lang)}</span>
                                            <Delta value={v.deltas?.carrier_approval_rate} good format={(x) => `${Math.round(x * 100)} pt`} />
                                        </td>
                                        <td className="num">{num(m.status_overrides, lang)}</td>
                                        <td className="num">{typeof m.errors_per_run === 'number' ? m.errors_per_run.toFixed(2) : '—'}<Delta value={v.deltas?.errors_per_run} good={false} format={(x) => x.toFixed(2)} /></td>
                                        <td className="num">{ms(m.avg_duration_ms)}<Delta value={v.deltas?.avg_duration_ms} good={false} format={ms} /></td>
                                        {withRuns.some((x) => typeof x.metrics?.cost_usd_per_ticket === 'number') && (
                                            <td className="num">{cost(m.cost_usd_per_ticket) || '—'}<Delta value={v.deltas?.cost_usd_per_ticket} good={false} format={(x) => `$${x.toFixed(4)}`} /></td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {evaluating && (
                <div className="asv-modal-overlay" role="dialog" aria-modal="true">
                    <div className="asv-modal">
                        <div className="asv-modal-header">
                            <h4><FaFlask /> {t('assistantVersions.evaluateTitle', 'Évaluer un changement d’instructions')}</h4>
                            <button type="button" className="asv-icon" onClick={() => setEvaluating(false)} title={t('assistantVersions.close', 'Fermer')}>✕</button>
                        </div>
                        <EvaluationPanel
                            assistantId={assistantId}
                            initialInstructions={currentInstructions || ''}
                            onApplied={() => { setEvaluating(false); load(); if (onApplied) onApplied(); }}
                        />
                    </div>
                </div>
            )}
            {withRuns.length === 0 && versions.length > 0 && (
                <div className="asv-muted asv-note">{t('assistantVersions.noRuns', 'Aucun run ne porte encore de version sur cette période : les runs sont marqués depuis le 17/09/2026.')}</div>
            )}
        </section>
    );
};

export default AssistantVersions;
